import { useState, useContext, useEffect, useMemo } from 'react';
import { AuthContext } from '../auth/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { FiRefreshCw } from 'react-icons/fi';
import { ALL_ROLES as ALL_ROLES_META, ROLE_LABEL, ROLE_META, TEAMS } from '../constants/roles';
import {
  DEFAULT_DEPARTMENT,
  normalizeDepartment,
  isValidDepartment,
  buildScopedRoleOptions,
  buildScopedTeamOptions,
  getDefaultTeamForRole,
  isAdminRole,
  resolveUserTeam,
  showsTeamPicker,
} from '../constants/departments';
import { getCustomTeams, saveCustomTeams, getCustomRoles, saveCustomRoles } from '../api/configApi';
import { getProjectsForAssignment } from '../api/projectApi';
import { getStoredDepartmentView } from '../components/DepartmentSwitcher';
import { getDepartmentAccent } from '../constants/departmentBoard';
import { useEnabledDepartments } from '../hooks/useEnabledDepartments';
import { COLORS as C, RADIUS } from '../constants/ui';
import {
  canManageUsers,
  isSelfServiceSteward,
  SELF_SERVICE_STEWARD_ROLES,
} from '../constants/userManagement';

function departmentPickerOptions(selectableDepartments, currentDepartment) {
  const options = [...(selectableDepartments || [])];
  const current = normalizeDepartment(currentDepartment);
  if (current && isValidDepartment(current) && !options.includes(current)) {
    options.push(current);
  }
  return options.sort((a, b) => String(a).localeCompare(String(b)));
}

function useLockBodyScroll() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
}

const avatarInitial = (name) => (name || '?').charAt(0).toUpperCase();
const avatarColor = (name) => {
  const colors = [C.accentInk,'#0891b2','#059669','#7c3aed','#db2777',C.warn,C.danger];
  let h = 0;
  for (let c of (name || '')) h = (h * 31 + c.charCodeAt(0)) % colors.length;
  return colors[h];
};

function ProjectDepartmentTag({ department }) {
  const dept = normalizeDepartment(department || DEFAULT_DEPARTMENT);
  const accent = getDepartmentAccent(dept);
  return (
    <span
      title={`Department: ${dept}`}
      style={{
        fontSize: '0.62rem',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 999,
        background: accent.bg,
        color: accent.color,
        border: `1px solid ${accent.border}`,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        lineHeight: 1.35,
        letterSpacing: '0.02em',
      }}
    >
      {dept}
    </span>
  );
}

function ProjectAssignmentRow({ project, checked, onToggle }) {
  const pid = String(project.projectid || project.project_id || '');
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        cursor: 'pointer',
        transition: 'background 0.12s',
        background: checked ? C.accentSoft : 'transparent',
        border: checked ? '1px solid #a9ddf5' : '1px solid transparent',
        borderRadius: 8,
        marginBottom: 4,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(pid)}
        style={{ width: 16, height: 16, cursor: 'pointer', accentColor: C.accent, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: '0.85rem',
            color: checked ? C.accentInk : C.ink,
            fontWeight: checked ? 700 : 500,
            minWidth: 0,
            wordBreak: 'break-word',
          }}
        >
          {project.projectname}
        </span>
        <ProjectDepartmentTag department={project.department} />
      </div>
      {checked && (
        <span style={{ fontSize: '0.65rem', color: C.done, fontWeight: 700, flexShrink: 0 }}>✓ assigned</span>
      )}
    </label>
  );
}

// ─── Pending Approvals Modal ──────────────────────────────────────────────────

const PendingModal = ({ onClose }) => {
  useLockBodyScroll();
  const qc = useQueryClient();
  const { user: currentUser, role: viewerRole } = useContext(AuthContext);
  const viewerIsAdmin = isAdminRole(viewerRole);
  const { selectableDepartments } = useEnabledDepartments();
  const [approveUser, setApproveUser] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(DEFAULT_DEPARTMENT);
  const departmentOptions = departmentPickerOptions(selectableDepartments, selectedDepartment);
  const [approveErrors, setApproveErrors] = useState({});

  const { data: pending = [], isLoading } = useQuery({
    queryKey: ['pending-users'],
    queryFn: () => api.get('/auth/users/pending').then(r => r.data),
  });

  const { data: customTeams = [] } = useQuery({ queryKey: ['custom-teams'], queryFn: getCustomTeams });
  const { data: customRoles = [] } = useQuery({ queryKey: ['custom-roles'], queryFn: getCustomRoles });
  const pendingTeamOptions = buildScopedTeamOptions(selectedDepartment, customTeams);
  const pendingRoleOptions = buildScopedRoleOptions(selectedDepartment, customRoles);

  const { data: projects = [] } = useQuery({
    queryKey: ['available-projects', selectedDepartment],
    queryFn: () =>
      getProjectsForAssignment({ department: selectedDepartment }).then((r) => r.data),
    enabled: !!approveUser,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, approve, role, team, department, projectIds }) =>
      api.put(`/auth/users/${id}/approve`, { approve, role, team, department, projectIds }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['pending-users'] });
      qc.invalidateQueries({ queryKey: ['team-users'] });
      if (variables?.approve) {
        const count = variables.projectIds?.length || 0;
        toast.success(
          count > 0
            ? `User approved and assigned to ${count} project${count > 1 ? 's' : ''}`
            : 'User approved. Assign projects anytime via Change Role.'
        );
      } else {
        toast.success('Done');
      }
      setApproveUser(null);
      setSelectedProjects([]);
    },
    onError: () => toast.error('Action failed'),
  });

  const handleApproveClick = (user) => {
    setApproveUser(user);
    setSelectedProjects([]);
    setApproveErrors({});
    const dept = normalizeDepartment(user.department || currentUser?.department || DEFAULT_DEPARTMENT);
    setSelectedDepartment(isValidDepartment(dept) ? dept : DEFAULT_DEPARTMENT);
    const scopedRoles = buildScopedRoleOptions(dept, customRoles);
    const validRoles = scopedRoles.map((r) => r.value);
    const initialRole = validRoles.includes(user.role) ? user.role : (scopedRoles[0]?.value || '');
    setSelectedRole(initialRole);
    const scopedTeams = buildScopedTeamOptions(dept, customTeams);
    const teamValues = scopedTeams.map((t) => t.value);
    setSelectedTeam(
      teamValues.includes(user.team) ? user.team : (getDefaultTeamForRole(initialRole, dept) || scopedTeams[0]?.value || '')
    );
  };

  useEffect(() => {
    if (!approveUser) return;
    const scopedRoles = buildScopedRoleOptions(selectedDepartment, customRoles);
    if (selectedRole && !scopedRoles.some((r) => r.value === selectedRole)) {
      const nextRole = scopedRoles[0]?.value || '';
      setSelectedRole(nextRole);
      setSelectedTeam(getDefaultTeamForRole(nextRole, selectedDepartment) || buildScopedTeamOptions(selectedDepartment, customTeams)[0]?.value || '');
      return;
    }
    const scopedTeams = buildScopedTeamOptions(selectedDepartment, customTeams);
    if (selectedTeam && !scopedTeams.some((t) => t.value === selectedTeam)) {
      setSelectedTeam(getDefaultTeamForRole(selectedRole, selectedDepartment) || scopedTeams[0]?.value || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when department/options change
  }, [selectedDepartment, approveUser, customRoles, customTeams]);

  const handleConfirmApprove = () => {
    if (!approveUser) return;
    const errors = {};
    const teamToSave = resolveUserTeam(selectedDepartment, selectedRole, selectedTeam);
    if (!selectedRole) errors.role = 'Role is required';
    if (showsTeamPicker(selectedDepartment) && !teamToSave) errors.team = 'Team is required';
    if (Object.keys(errors).length > 0) { setApproveErrors(errors); return; }
    setApproveErrors({});
    approveMutation.mutate({
      id: approveUser.id,
      approve: true,
      role: selectedRole,
      team: teamToSave,
      department: selectedDepartment,
      projectIds: selectedProjects,
    });
  };

  const toggleProject = (projectId) => {
    const id = String(projectId);
    setSelectedProjects(prev =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  // Approval dialog with project selection
  if (approveUser) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '1.75rem 2rem',
          width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.ink }}>
              Approve User
            </h2>
            <button onClick={() => setApproveUser(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
          </div>

          <div style={{ background: C.sunken, borderRadius: 10, padding: '1rem', marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: avatarColor(approveUser.email),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                {avatarInitial(approveUser.email)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', color: C.ink }}>{approveUser.email}</div>
                <div style={{ fontSize: '0.78rem', color: C.inkFaint, marginTop: 2 }}>
                  Requested: {ROLE_LABEL[approveUser.role] || approveUser.role}
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: C.ink, marginBottom: '0.5rem' }}>
              Department
            </label>
            {viewerIsAdmin ? (
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                style={{ width: '100%', padding: '0.65rem 0.75rem', borderRadius: 8, border: `1px solid ${C.line}`, fontSize: '0.9rem', fontWeight: 500, background: '#fff', cursor: 'pointer' }}
              >
                {departmentOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            ) : (
              <div style={{ padding: '0.65rem 0.75rem', borderRadius: 8, border: `1px solid ${C.line}`, fontSize: '0.9rem', fontWeight: 600, background: C.sunken, color: C.inkSoft }}>
                {selectedDepartment}
              </div>
            )}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: showsTeamPicker(selectedDepartment) ? 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))' : '1fr',
            gap: '0.75rem',
            marginBottom: '1.25rem',
          }}>
            <div>
              <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: approveErrors.role ? C.danger : C.ink, marginBottom: '0.5rem' }}>
                Assign Role <span style={{ color: C.danger }}>*</span>
              </label>
              <select
                value={selectedRole}
                onChange={(e) => {
                  const nextRole = e.target.value;
                  setSelectedRole(nextRole);
                  setSelectedTeam(getDefaultTeamForRole(nextRole, selectedDepartment) || selectedTeam);
                  setApproveErrors((p) => ({ ...p, role: undefined }));
                }}
                style={{ width: '100%', padding: '0.65rem 0.75rem', borderRadius: 8, border: `1px solid ${approveErrors.role ? C.danger : C.line}`, fontSize: '0.88rem', fontWeight: 500, background: '#fff', cursor: 'pointer' }}
              >
                <option value="">— Select role —</option>
                {pendingRoleOptions.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {approveErrors.role && <p style={{ color: C.danger, fontSize: '0.72rem', margin: '4px 0 0' }}>{approveErrors.role}</p>}
              {!showsTeamPicker(selectedDepartment) && (
                <p style={{ fontSize: '0.72rem', color: C.inkFaint, margin: '4px 0 0' }}>
                  Work group is set automatically from role.
                </p>
              )}
            </div>
            {showsTeamPicker(selectedDepartment) && (
              <div>
                <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: approveErrors.team ? C.danger : C.ink, marginBottom: '0.5rem' }}>
                  Assign Team <span style={{ color: C.danger }}>*</span>
                </label>
                <select
                  value={selectedTeam}
                  onChange={(e) => { setSelectedTeam(e.target.value); setApproveErrors(p => ({ ...p, team: undefined })); }}
                  style={{ width: '100%', padding: '0.65rem 0.75rem', borderRadius: 8, border: `1px solid ${approveErrors.team ? C.danger : C.line}`, fontSize: '0.9rem', fontWeight: 500, background: '#fff', cursor: 'pointer' }}
                >
                  <option value="">— Select team —</option>
                  {pendingTeamOptions.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                {approveErrors.team && <p style={{ color: C.danger, fontSize: '0.72rem', margin: '4px 0 0' }}>{approveErrors.team}</p>}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: C.ink, marginBottom: '0.75rem' }}>
              Assign to Projects <span style={{ fontWeight: 500, color: C.inkFaint }}>(optional)</span>
            </label>
            {projects.length === 0 ? (
              <div style={{ padding: '1rem', background: C.sunken, borderRadius: 8, color: C.inkSoft, fontSize: '0.85rem', border: `1px solid ${C.line}` }}>
                No projects in {selectedDepartment} yet. You can approve now and assign projects later via Change Role.
              </div>
            ) : (
              <div style={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${C.line}`, borderRadius: 10, padding: '0.5rem' }}>
                {projects.map((p) => (
                  <ProjectAssignmentRow
                    key={p.projectid}
                    project={p}
                    checked={selectedProjects.includes(String(p.projectid))}
                    onToggle={toggleProject}
                  />
                ))}
              </div>
            )}
            <p style={{ fontSize: '0.75rem', color: C.inkFaint, marginTop: '0.5rem' }}>
              Optional — assign now or add projects later from the team member&apos;s Change Role screen.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setApproveUser(null)}
              style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.line}`,
                background: '#fff', color: C.inkSoft, fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
            <button onClick={handleConfirmApprove}
              disabled={approveMutation.isPending}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none',
                background: C.done,
                color: '#fff', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 700 }}>
              {approveMutation.isPending ? 'Approving...' : `Approve${selectedProjects.length > 0 ? ` (${selectedProjects.length} project${selectedProjects.length > 1 ? 's' : ''})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.75rem 2rem',
        width: '100%', maxWidth: 560, maxHeight: '80vh', overflowY: 'auto',
        boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.ink }}>
            Pending Approvals
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', color: C.inkFaint, padding: '2rem' }}>Loading…</div>
        ) : pending.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', background: '#f0fdf4', borderRadius: 10,
            color: C.done, fontWeight: 600 }}>
            ✅ No pending approvals
          </div>
        ) : pending.map(u => {
          const requestedRole = u.role || 'developer';
          const roleMeta = ROLE_META[requestedRole] || { color: C.inkSoft, bg: C.sunken, label: requestedRole };
          return (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12,
              padding: '1rem 0', borderBottom: `1px solid ${C.sunken}` }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: avatarColor(u.email),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0 }}>
                {avatarInitial(u.email)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                <div style={{ fontSize: '0.78rem', color: C.inkSoft, marginTop: 2 }}>
                  Requested: <span style={{ background: roleMeta.bg, color: roleMeta.color, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>{roleMeta.label}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => handleApproveClick(u)}
                  style={{ padding: '6px 14px', borderRadius: 8, border: 'none',
                    background: C.done, color: '#fff', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}>
                  Approve
                </button>
                <button onClick={() => approveMutation.mutate({ id: u.id, approve: false })}
                  style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${C.line}`,
                    background: '#fff', color: C.danger, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}>
                  Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Invite Modal ─────────────────────────────────────────────────────────────
// Simplified: just email + optional project. Role is assigned after joining.

const InviteModal = ({ onClose }) => {
  useLockBodyScroll();
  const { user: currentUser, role: viewerRole } = useContext(AuthContext);
  const viewerIsAdmin = isAdminRole(viewerRole);
  const { selectableDepartments } = useEnabledDepartments();
  const [form, setForm] = useState({
    email: '',
    department: currentUser?.department || DEFAULT_DEPARTMENT,
  });
  const departmentOptions = departmentPickerOptions(selectableDepartments, form.department);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        email: form.email || undefined,
        department: form.department,
      };
      const res = await api.post('/auth/invite', body);
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create invite');
    }
    setSaving(false);
  };

  const copyMessage = () => {
    const expiry = new Date(result.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const msg = `Hi,

You have been invited to join Team Space — our team's project and sprint management tool.

Click the link below to create your account and get started:
${result.inviteUrl}

This link is valid until ${expiry}. Once you register, your role will be assigned by the team admin.

Welcome aboard!
The Team Space team`;
    navigator.clipboard.writeText(msg);
    toast.success('Invite message copied!');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.inviteUrl);
    toast.success('Invite link copied!');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.75rem 2rem',
        width: '100%', maxWidth: 420, boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.ink }}>
            Invite Team Member
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
        </div>

        {!result ? (
          <form onSubmit={handleCreate}>
            <p style={{ fontSize: '0.82rem', color: C.inkSoft, margin: '0 0 16px' }}>
              Invite someone to join your department. You can assign their <strong>role</strong> after they join
              from the Team page.
            </p>

            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: C.ink, marginBottom: 4 }}>
              Department
            </label>
            {viewerIsAdmin ? (
              <select
                value={form.department}
                onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.line}`, borderRadius: 8,
                  fontSize: '0.85rem', marginBottom: 14, boxSizing: 'border-box', background: C.sunken }}
              >
                {departmentOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            ) : (
              <div style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.line}`, borderRadius: 8,
                fontSize: '0.85rem', marginBottom: 14, boxSizing: 'border-box', background: C.sunken, color: C.inkSoft, fontWeight: 600 }}>
                {form.department}
              </div>
            )}

            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: C.ink, marginBottom: 4 }}>
              Email (optional — pre-fills their registration)
            </label>
            <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              type="email" placeholder="colleague@company.com"
              style={{ width: '100%', padding: '8px 12px', border: `1px solid ${C.line}`, borderRadius: 8,
                fontSize: '0.85rem', marginBottom: 14, boxSizing: 'border-box', background: C.sunken }} />

            <div style={{ background: C.warnSoft, border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: '0.78rem', color: '#92400e' }}>
              Invite link expires in <strong>2 days</strong>. Multiple team members can register using the same link.
            </div>

            <button type="submit" disabled={saving}
              style={{ width: '100%', padding: '10px', background: C.accentInk, color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
              {saving ? 'Generating…' : 'Generate Invite Link'}
            </button>
          </form>
        ) : (
          <div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
              padding: '1rem', marginBottom: 16, textAlign: 'center' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.doneSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', color: C.done, fontWeight: 800, fontSize: '1.1rem' }}>✓</div>
              <div style={{ fontWeight: 700, color: C.done, fontSize: '0.9rem' }}>Invite link ready!</div>
              <div style={{ fontSize: '0.78rem', color: C.inkSoft, marginTop: 4 }}>
                {form.email && <>For: <strong>{form.email}</strong></>}
                {result.department && <> · Dept: <strong>{result.department}</strong></>}
              </div>
              <div style={{ fontSize: '0.72rem', color: C.inkFaint, marginTop: 6 }}>
                Assign their role after they join from the Team page.
              </div>
            </div>

            <div style={{ background: C.sunken, border: `1px solid ${C.line}`, borderRadius: 8,
              padding: '10px 12px', marginBottom: 12, wordBreak: 'break-all',
              fontSize: '0.75rem', color: C.ink, fontFamily: 'monospace' }}>
              {result.inviteUrl}
            </div>

            <button onClick={copyMessage}
              style={{ width: '100%', padding: '10px', background: C.accentInk, color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 8 }}>
              Copy Invite Message
            </button>
            <button onClick={copyLink}
              style={{ width: '100%', padding: '9px', background: C.sunken, color: C.accentInk,
                border: '1px solid #a9ddf5', borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', marginBottom: 8 }}>
              Copy Link Only
            </button>
            <button onClick={() => { setResult(null); setForm({ email: '', department: currentUser?.department || DEFAULT_DEPARTMENT }); }}
              style={{ width: '100%', padding: '8px', background: C.sunken, color: C.inkSoft,
                border: `1px solid ${C.line}`, borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
              Create Another Invite
            </button>

            <p style={{ fontSize: '0.75rem', color: C.inkFaint, marginTop: 12, textAlign: 'center' }}>
              Share via Slack, WhatsApp, or email. Expires {new Date(result.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Reset Password Modal ─────────────────────────────────────────────────────

const ResetPasswordModal = ({ user, onClose }) => {
  useLockBodyScroll();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerateLink = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await api.post(`/users/${user.id}/reset-password-link`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate reset link');
    }
    setLoading(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.resetUrl);
    toast.success('Reset link copied!');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.75rem 2rem',
        width: '100%', maxWidth: 440, boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: C.ink }}>
            🔑 Reset Password
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
        </div>

        {!result ? (
          <>
            <p style={{ fontSize: '0.85rem', color: C.inkSoft, margin: '0 0 16px' }}>
              Generate a password reset link for <strong>{user?.full_name || user?.email}</strong>
            </p>

            {error && (
              <div style={{ padding: '10px 12px', background: C.dangerSoft, border: '1px solid #fecaca',
                borderRadius: 8, color: C.danger, fontSize: '0.82rem', marginBottom: 12 }}>
                {error}
              </div>
            )}

            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, 
              padding: '1rem', marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#0369a1' }}>How it works:</h4>
              <ol style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.8rem', color: C.inkSoft, lineHeight: 1.6 }}>
                <li>Click "Generate Reset Link" below</li>
                <li>Share the link with the user (Slack, WhatsApp, etc.)</li>
                <li>User clicks the link and sets their own new password</li>
              </ol>
            </div>

            <div style={{ background: C.warnSoft, border: '1px solid #fde68a', borderRadius: 8, 
              padding: '10px 12px', marginBottom: 16, fontSize: '0.78rem', color: '#92400e' }}>
              ⏱ The reset link expires in <strong>24 hours</strong> and can only be used once.
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose}
                style={{ flex: 1, padding: '10px', background: C.sunken, color: C.inkSoft,
                  border: `1px solid ${C.line}`, borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleGenerateLink} disabled={loading}
                style={{ flex: 1, padding: '10px', background: C.accentInk, color: '#fff',
                  border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                {loading ? 'Generating…' : 'Generate Reset Link'}
              </button>
            </div>
          </>
        ) : (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: '3rem', marginBottom: 8 }}>🔗</div>
              <h3 style={{ margin: '0 0 4px', color: C.done, fontSize: '1.05rem' }}>Reset Link Generated!</h3>
              <p style={{ fontSize: '0.8rem', color: C.inkSoft, margin: 0 }}>
                For <strong>{result.email}</strong>
              </p>
            </div>
            
            <div style={{ background: C.sunken, border: `1px solid ${C.line}`, borderRadius: 8,
              padding: '10px 12px', marginBottom: 12, wordBreak: 'break-all',
              fontSize: '0.75rem', color: C.ink, fontFamily: 'monospace' }}>
              {result.resetUrl}
            </div>
            
            <button 
              onClick={copyLink}
              style={{ width: '100%', padding: '10px', background: C.accentInk, color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 8 }}>
              📋 Copy Reset Link
            </button>

            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, 
              padding: '10px 12px', marginBottom: 12, fontSize: '0.78rem', color: '#166534' }}>
              ✅ Share this link with the user via Slack, WhatsApp, or any secure channel.<br/>
              They can click it to set their own new password.
            </div>

            <p style={{ fontSize: '0.72rem', color: C.inkFaint, textAlign: 'center', margin: '0 0 12px' }}>
              Expires: {new Date(result.expiresAt).toLocaleString()}
            </p>
            
            <button onClick={onClose}
              style={{ width: '100%', padding: '8px', background: C.sunken, color: C.inkSoft,
                border: `1px solid ${C.line}`, borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Edit User Modal (Role + Project Assignments) ────────────────────────────

const EditUserModal = ({ user, onClose }) => {
  useLockBodyScroll();
  const qc = useQueryClient();
  const { user: currentUser, role: viewerRole } = useContext(AuthContext);
  const { selectableDepartments } = useEnabledDepartments();
  const viewerRoleLower = (viewerRole || '').toLowerCase();
  const viewerIsAdmin = isAdminRole(viewerRole);
  const isSelf = Number(currentUser?.id) === Number(user?.id);
  const canSelfEditRole = isSelf && isSelfServiceSteward(viewerRole);

  const initialDepartment = normalizeDepartment(user?.department || DEFAULT_DEPARTMENT);
  const [department, setDepartment] = useState(
    isValidDepartment(initialDepartment) ? initialDepartment : DEFAULT_DEPARTMENT
  );
  const departmentOptions = departmentPickerOptions(selectableDepartments, department);
  const validRoleValues = buildScopedRoleOptions(department).map((r) => r.value);
  const [role, setRole] = useState(
    validRoleValues.includes(user?.role) ? user.role : (validRoleValues[0] || 'developer')
  );
  const [team, setTeam] = useState(user?.team || getDefaultTeamForRole(user?.role, initialDepartment) || '');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  const { data: customTeams = [] } = useQuery({ queryKey: ['custom-teams'], queryFn: getCustomTeams });
  const { data: customRoles = [] } = useQuery({ queryKey: ['custom-roles'], queryFn: getCustomRoles });
  const editTeamOptions = buildScopedTeamOptions(department, customTeams);
  const canAssignRolesToOthers = canManageUsers(viewerRole) && !isSelf;

  const editRoleOptions = (() => {
    const scoped = buildScopedRoleOptions(department, customRoles);
    if (canSelfEditRole) {
      return scoped.filter((r) => SELF_SERVICE_STEWARD_ROLES.includes(r.value));
    }
    return scoped;
  })();

  useEffect(() => {
    const scopedRoles = buildScopedRoleOptions(department, customRoles);
    if (role && !scopedRoles.some((r) => r.value === role)) {
      const nextRole = scopedRoles[0]?.value || 'developer';
      setRole(nextRole);
      setTeam(getDefaultTeamForRole(nextRole, department) || '');
      return;
    }
    const scopedTeams = buildScopedTeamOptions(department, customTeams);
    if (team && !scopedTeams.some((t) => t.value === team)) {
      setTeam(getDefaultTeamForRole(role, department) || scopedTeams[0]?.value || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when department/options change
  }, [department, customRoles, customTeams]);

  const { data: catalogProjects = [], isLoading: loadingCatalog } = useQuery({
    queryKey: ['projects-for-edit', department],
    queryFn: () =>
      getProjectsForAssignment({ department }).then((r) => r.data),
  });

  const { data: userProjects = [], isLoading: loadingUserProjects } = useQuery({
    queryKey: ['user-projects', user?.id],
    queryFn: () => api.get(`/users/${user.id}/projects`).then(r => r.data),
    enabled: !!user?.id,
  });

  const loadingProjects = loadingCatalog || loadingUserProjects;

  const projects = useMemo(() => {
    const deptNorm = normalizeDepartment(department);
    const byId = new Map();
    (catalogProjects || []).forEach((p) => {
      const id = String(p.projectid || p.project_id || '');
      if (id) byId.set(id, p);
    });
    (userProjects || []).forEach((p) => {
      const id = String(p.projectid || p.project_id || '');
      const pDept = normalizeDepartment(p.department || DEFAULT_DEPARTMENT);
      if (id && !byId.has(id) && pDept === deptNorm) {
        byId.set(id, { ...p, projectid: p.projectid || p.project_id });
      }
    });
    return [...byId.values()].sort((a, b) =>
      String(a.projectname || '').localeCompare(String(b.projectname || ''))
    );
  }, [catalogProjects, userProjects, department]);

  useEffect(() => {
    if (!user?.id || loadingCatalog) return;
    const visibleIds = new Set(
      (catalogProjects || [])
        .map((p) => String(p.projectid || p.project_id || ''))
        .filter(Boolean)
    );
    setSelectedProjects(
      (userProjects || [])
        .map((p) => String(p.projectid || p.project_id || ''))
        .filter((id) => visibleIds.has(id))
    );
  }, [user?.id, userProjects, department, catalogProjects, loadingCatalog]);

  const toggleProject = (projectId) => {
    const id = String(projectId);
    setSelectedProjects(prev =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    const initialRole = (user?.role || '').toLowerCase();
    const roleChanged = role.toLowerCase() !== initialRole;
    const initialDept = user?.department || DEFAULT_DEPARTMENT;
    const departmentChanged = department !== initialDept;

    try {
      if (viewerIsAdmin && departmentChanged) {
        await api.put(`/users/${user.id}/department`, { department });
      }
      if (!isSelf || roleChanged) {
        await api.put(`/users/${user.id}/role`, {
          role,
          ...(departmentChanged ? { department } : {}),
        });
      }
      await api.put(`/users/${user.id}/team`, { team: resolveUserTeam(department, role, team) });
      await api.put(`/users/${user.id}/projects`, { projectIds: selectedProjects });
      qc.invalidateQueries({ queryKey: ['team-users'] });
      qc.invalidateQueries({ queryKey: ['user-projects', user.id] });
      if (isSelf && roleChanged) {
        window.dispatchEvent(new Event('auth:resync'));
      }
      toast.success(isSelf ? 'Your profile was updated' : 'User updated successfully');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update user');
    }
    setSaving(false);
  };

  const showTeamPicker = showsTeamPicker(department);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.75rem 2rem',
        width: '100%', maxWidth: 580, boxShadow: '0 25px 60px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.ink }}>
            ✏️ Edit User
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
        </div>

        <p style={{ fontSize: '0.85rem', color: C.inkSoft, margin: '0 0 20px', fontWeight: 600 }}>
          {user?.full_name || user?.email}
        </p>

        {isSelf && (
          <p style={{
            fontSize: '0.8rem', color: C.accentInk, background: C.accentSoft, borderRadius: 8,
            padding: '10px 12px', margin: '0 0 16px', lineHeight: 1.45,
          }}>
            You are editing your own profile. Update your {showTeamPicker ? 'team and ' : ''}project access below.
            {canSelfEditRole
              ? ' Role changes are limited to department steward roles. To assign Designer, Trainee, PR Manager, or other roles, open another team member’s card.'
              : ' Your role cannot be changed here.'}
          </p>
        )}
        {canAssignRolesToOthers && (
          <p style={{
            fontSize: '0.8rem', color: C.inkSoft, background: C.sunken, borderRadius: 8,
            padding: '10px 12px', margin: '0 0 16px', lineHeight: 1.45,
          }}>
            Assign roles and projects for <strong>{department}</strong>.
            Only projects in this department are shown below.
          </p>
        )}

        {/* Department */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: C.ink, marginBottom: 6 }}>
            Department
          </label>
          {viewerIsAdmin && !isSelf ? (
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', border: `1px solid ${C.line}`, borderRadius: 8,
                fontSize: '0.9rem', background: C.sunken,
              }}
            >
              {departmentOptions.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          ) : (
            <div style={{
              width: '100%', padding: '10px 12px', border: `1px solid ${C.line}`, borderRadius: 8,
              fontSize: '0.9rem', background: C.sunken, color: C.inkSoft, fontWeight: 600,
            }}>
              {department}
            </div>
          )}
        </div>

        {/* Role (+ Team for Engineering only) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: showTeamPicker ? 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))' : '1fr',
          gap: '0.75rem',
          marginBottom: 20,
        }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: C.ink, marginBottom: 6 }}>Role</label>
            <select
              value={role}
              onChange={(e) => {
                const nextRole = e.target.value;
                setRole(nextRole);
                setTeam(getDefaultTeamForRole(nextRole, department) || team);
              }}
              disabled={isSelf && !canSelfEditRole}
              style={{
                width: '100%', padding: '10px 12px', border: `1px solid ${C.line}`, borderRadius: 8,
                fontSize: '0.88rem', background: isSelf && !canSelfEditRole ? C.sunken : C.sunken,
              }}
            >
              {editRoleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {!showTeamPicker && (
              <p style={{ fontSize: '0.72rem', color: C.inkFaint, margin: '4px 0 0' }}>
                Work group is set automatically from role.
              </p>
            )}
          </div>
          {showTeamPicker && (
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: C.ink, marginBottom: 6 }}>Team</label>
              <select value={team} onChange={e => setTeam(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: '0.9rem', background: C.sunken }}>
                <option value="">— No team —</option>
                {editTeamOptions.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Project Assignments */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: C.ink }}>Project Assignments</label>
            {selectedProjects.length > 0 && (
              <span style={{ fontSize: '0.72rem', fontWeight: 700, background: C.accentSoft, color: C.accentInk, padding: '2px 8px', borderRadius: 10 }}>
                {selectedProjects.length} assigned
              </span>
            )}
          </div>
          {loadingProjects ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: C.inkFaint, fontSize: '0.85rem' }}>Loading projects…</div>
          ) : projects.length === 0 ? (
            <div style={{ padding: '1rem', background: C.warnSoft, borderRadius: 8, color: '#92400e', fontSize: '0.85rem' }}>
              No projects in {department} yet. Create a project for this department first, or switch department above.
            </div>
          ) : (
            <div style={{ border: `1px solid ${C.line}`, borderRadius: 8, overflow: 'hidden', padding: '0.35rem' }}>
              {projects.map((proj) => (
                <ProjectAssignmentRow
                  key={proj.projectid}
                  project={proj}
                  checked={selectedProjects.includes(String(proj.projectid))}
                  onToggle={toggleProject}
                />
              ))}
            </div>
          )}
          <p style={{ fontSize: '0.72rem', color: C.inkFaint, marginTop: 6 }}>
            Check to assign · Uncheck to remove access
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '10px', background: C.sunken, color: C.inkSoft,
              border: `1px solid ${C.line}`, borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: '10px', background: C.accentInk, color: '#fff',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Manage Teams / Roles Config Modal ───────────────────────────────────────

const ManageConfigModal = ({ mode, onClose }) => {
  useLockBodyScroll();
  const qc = useQueryClient();
  const isTeam = mode === 'team';
  const title = isTeam ? 'Manage Teams' : 'Manage Roles';

  const defaultItems = isTeam
    ? TEAMS.filter(t => t.value !== 'Any').map(t => ({ value: t.value, label: t.label, isDefault: true }))
    : ALL_ROLES_META.map(r => ({ value: r.value, label: r.label, isDefault: true }));

  const { data: customItems = [], isLoading, refetch } = useQuery({
    queryKey: [isTeam ? 'custom-teams' : 'custom-roles'],
    queryFn: isTeam ? getCustomTeams : getCustomRoles,
  });

  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const allItems = [
    ...defaultItems,
    ...customItems.map(c => ({ ...c, isDefault: false })),
  ];

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    const value = label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    if (allItems.some(i => i.value === value || i.label.toLowerCase() === label.toLowerCase())) {
      toast.error('Already exists');
      return;
    }
    setSaving(true);
    try {
      const updated = [...customItems, { value, label }];
      if (isTeam) await saveCustomTeams(updated);
      else await saveCustomRoles(updated);
      await refetch();
      qc.invalidateQueries({ queryKey: ['app-config'] });
      setNewLabel('');
      toast.success(`${isTeam ? 'Team' : 'Role'} "${label}" added`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async (value) => {
    setSaving(true);
    try {
      const updated = customItems.filter(c => c.value !== value);
      if (isTeam) await saveCustomTeams(updated);
      else await saveCustomRoles(updated);
      await refetch();
      qc.invalidateQueries({ queryKey: ['app-config'] });
      toast.success('Removed');
    } catch (err) {
      toast.error('Failed to remove');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.75rem 2rem',
        width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.ink }}>
            {isTeam ? '🏷️' : '🎭'} {title}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.inkFaint }}>✕</button>
        </div>

        {/* Add new */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <input
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder={isTeam ? 'e.g. Mobile App' : 'e.g. Data Engineer'}
            style={{ flex: 1, padding: '9px 12px', border: `1px solid ${C.line}`, borderRadius: 8,
              fontSize: '0.9rem', background: C.sunken }}
          />
          <button onClick={handleAdd} disabled={saving || !newLabel.trim()}
            style={{ padding: '9px 16px', background: newLabel.trim() ? '#059669' : C.inkFaint,
              color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem',
              cursor: newLabel.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>
            + Add
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', color: C.inkFaint, padding: '1rem' }}>Loading…</div>
        ) : (
          <div style={{ border: `1px solid ${C.line}`, borderRadius: 8, overflow: 'hidden' }}>
            {allItems.map((item, idx) => (
              <div key={item.value} style={{
                display: 'flex', alignItems: 'center', padding: '10px 14px', gap: 10,
                borderBottom: idx < allItems.length - 1 ? `1px solid ${C.sunken}` : 'none',
                background: item.isDefault ? '#fafafa' : C.warnSoft,
              }}>
                <span style={{ flex: 1, fontSize: '0.88rem', fontWeight: 600, color: C.ink }}>
                  {item.label}
                </span>
                <span style={{ fontSize: '0.68rem', color: C.inkFaint, fontFamily: 'monospace' }}>
                  {item.value}
                </span>
                {item.isDefault ? (
                  <span style={{ fontSize: '0.65rem', color: C.inkSoft, background: C.sunken,
                    padding: '2px 7px', borderRadius: 6, fontWeight: 600 }}>built-in</span>
                ) : (
                  <button onClick={() => handleDelete(item.value)} disabled={saving}
                    style={{ fontSize: '0.72rem', color: C.danger, background: C.dangerSoft,
                      border: '1px solid #fecaca', borderRadius: 6, padding: '3px 8px',
                      cursor: 'pointer', fontWeight: 600 }}>Remove</button>
                )}
              </div>
            ))}
          </div>
        )}

        <p style={{ fontSize: '0.72rem', color: C.inkFaint, marginTop: 10 }}>
          Built-in items cannot be removed · Custom items appear in all dropdowns
        </p>

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button onClick={onClose}
            style={{ padding: '8px 20px', background: C.accentInk, color: '#fff',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Team Roster ─────────────────────────────────────────────────────────

const UserManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser, role: viewerRole } = useContext(AuthContext);
  const canManage = canManageUsers(viewerRole);
  const viewerIsAdmin = isAdminRole(viewerRole);
  const rosterDepartment = currentUser?.department || DEFAULT_DEPARTMENT;
  const qc = useQueryClient();
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await qc.invalidateQueries({ queryKey: ['team-users'] });
    await qc.invalidateQueries({ queryKey: ['pending-users'] });
    setTimeout(() => setRefreshing(false), 600);
  };
  const [showPending, setShowPending] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [showConfig, setShowConfig] = useState(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['team-users'],
    queryFn: () => api.get('/users').then(r => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-users'] });
      toast.success('User removed from organisation');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not remove user'),
  });

  const { data: pending = [] } = useQuery({
    queryKey: ['pending-users'],
    queryFn: () => api.get('/auth/users/pending').then(r => r.data),
    enabled: canManage,
  });

  const { data: customRoles = [] } = useQuery({ queryKey: ['custom-roles'], queryFn: getCustomRoles });

  const roleFilterOptions = useMemo(() => {
    const deptView = getStoredDepartmentView();
    const dept = viewerIsAdmin
      ? (deptView || rosterDepartment)
      : (currentUser?.department || DEFAULT_DEPARTMENT);
    const catalog = buildScopedRoleOptions(dept, customRoles);
    const fromUsers = users.map((u) => u.role?.toLowerCase()).filter(Boolean);
    const seen = new Set(['all']);
    const chips = [{ value: 'all', label: 'All Roles' }];
    catalog.forEach((r) => {
      if (!seen.has(r.value)) {
        seen.add(r.value);
        chips.push({ value: r.value, label: r.label });
      }
    });
    fromUsers.forEach((rv) => {
      if (!seen.has(rv)) {
        seen.add(rv);
        const meta = ROLE_META[rv];
        chips.push({ value: rv, label: meta?.label || rv });
      }
    });
    return chips;
  }, [users, customRoles, rosterDepartment, viewerIsAdmin, currentUser?.department]);

  const filtered = users.filter(u => {
    if (roleFilter !== 'all' && u.role?.toLowerCase() !== roleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (u.full_name || '').toLowerCase().includes(q) ||
             (u.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div style={{ padding: '1.75rem 2rem', background: C.sunken, minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: C.ink }}>User Management</h1>
          <p style={{ margin: '3px 0 0', color: C.inkSoft, fontSize: '0.82rem' }}>
            {users.length} member{users.length !== 1 ? 's' : ''} · click a card to view workload
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={handleRefresh}
            disabled={refreshing || isLoading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fff', color: C.ink, border: `1px solid ${C.line}`, borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <FiRefreshCw size={14} style={{ transition: 'transform 0.5s', transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)' }} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          {canManage && (
            <>
              <button onClick={() => setShowConfig('team')}
                style={{ padding: '8px 14px', background: '#fff', color: C.ink,
                  border: `1px solid ${C.line}`, borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                + Team
              </button>
              <button onClick={() => setShowConfig('role')}
                style={{ padding: '8px 14px', background: '#fff', color: C.ink,
                  border: `1px solid ${C.line}`, borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                + Role
              </button>
            </>
          )}
          <button onClick={() => setShowInvite(true)}
            style={{ padding: '8px 18px', background: '#059669', color: '#fff',
              border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
            Invite Member
          </button>
          {canManage && (
            <button onClick={() => setShowPending(true)}
              style={{ position: 'relative', padding: '8px 18px', background: C.accentInk, color: '#fff',
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              Pending Approvals
              {pending.length > 0 && (
                <span style={{ position: 'absolute', top: -6, right: -6, background: C.danger, color: '#fff',
                  borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800 }}>
                  {pending.length}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          style={{ flex: '1 1 200px', padding: '7px 12px', border: `1px solid ${C.line}`,
            borderRadius: 8, fontSize: '0.83rem', background: '#fff' }} />
        {roleFilterOptions.map(({ value, label }) => {
          const m = ROLE_META[value];
          const active = roleFilter === value;
          return (
            <button key={value} onClick={() => setRoleFilter(value)}
              style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid',
                borderColor: active ? (m?.color || C.ink) : C.line,
                background: active ? (m?.bg || C.sunken) : '#fff',
                color: active ? (m?.color || C.ink) : C.inkSoft,
                fontWeight: active ? 700 : 500, fontSize: '0.76rem', cursor: 'pointer' }}>
              {label}
            </button>
          );
        })}
      </div>

      {/* Team grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: C.inkFaint }}>Loading team…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: C.inkFaint }}>No members match your filter.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 16 }}>
          {filtered.map(u => {
            const rm = ROLE_META[u.role?.toLowerCase()] || { color: C.ink, bg: C.sunken, label: u.role || 'Member' };
            const ac = avatarColor(u.full_name || u.email);
            return (
              <div key={u.id}
                style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.line}`,
                  padding: '1.25rem', transition: 'all 0.15s',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(18, 114, 179,0.12)'; e.currentTarget.style.borderColor = '#a9ddf5'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'; e.currentTarget.style.borderColor = C.line; }}>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: ac,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.25rem', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                    {avatarInitial(u.full_name || u.email)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: C.ink,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.full_name || u.email}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: C.inkFaint, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ background: rm.bg, color: rm.color, padding: '2px 10px',
                    borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>
                    {rm.label}
                  </span>
                  {/* Change Role: superadmin is untouchable by anyone — there is
                      no role above it to authorize the change. An admin target
                      may be changed, but only by another admin/superadmin, which
                      the button-level check below enforces the same way the
                      server does. Self-change allowed only for manager-level
                      roles (not superadmin/admin). */}
                  {canManage &&
                   !u.isProtected &&
                   u.role?.toLowerCase() !== 'superadmin' &&
                   (u.role?.toLowerCase() !== 'admin' || viewerIsAdmin) &&
                   (currentUser?.id !== u.id || isSelfServiceSteward(viewerRole)) && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setEditingUser(u); }}
                      style={{
                        fontSize: '0.68rem', fontWeight: 600, padding: '3px 8px',
                        borderRadius: 6, border: '1px solid #a9ddf5', background: C.accentSoft,
                        color: C.accentInk, cursor: 'pointer',
                      }}
                    >
                      Change Role
                    </button>
                  )}
                </div>
                {u.team && (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: '0.65rem', color: C.inkFaint }}>Team:</span>
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: C.inkSoft,
                      background: C.sunken, borderRadius: 10, padding: '1px 8px' }}>
                      {u.team.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                  </div>
                )}

                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => navigate(`/users/${u.id}/profile`)}
                    style={{ fontSize: '0.72rem', color: C.accentInk, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    View Profile →
                  </button>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {/* Reset Password button - for managers/admins */}
                    {canManage && currentUser?.id !== u.id && 
                     !u.isProtected && !['superadmin', 'admin'].includes(u.role?.toLowerCase()) && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setResetPasswordUser(u); }}
                        style={{
                          fontSize: '0.68rem', fontWeight: 600, padding: '4px 8px',
                          borderRadius: 6, border: '1px solid #fde68a', background: '#fefce8',
                          color: '#a16207', cursor: 'pointer',
                        }}
                      >
                        🔑 Reset Pwd
                      </button>
                    )}
                    {/* Hide Remove button for protected admins (defined in env) and superadmin/admin roles */}
                    {canManage && currentUser?.id !== u.id && 
                     !u.isProtected && !['superadmin', 'admin'].includes(u.role?.toLowerCase()) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Remove ${u.email} from Team Space? They will lose access immediately.`)) return;
                          deleteMutation.mutate(u.id);
                        }}
                        style={{
                          fontSize: '0.68rem', fontWeight: 700, padding: '4px 10px',
                          borderRadius: 8, border: '1px solid #fecaca', background: '#fff5f5',
                          color: C.danger, cursor: 'pointer',
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showPending && <PendingModal onClose={() => setShowPending(false)} />}
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
      {editingUser && <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} />}
      {resetPasswordUser && <ResetPasswordModal user={resetPasswordUser} onClose={() => setResetPasswordUser(null)} />}
      {showConfig && <ManageConfigModal mode={showConfig} onClose={() => setShowConfig(null)} />}
    </div>
  );
};

export default UserManagement;
